import { PageTitle } from '_metronic/layout/core'
import { WithChildren } from '_metronic/helpers'
import { Scanner } from '@yudiel/react-qr-scanner'
import Switch from 'react-switch'

import PageBreadCrumb from 'Presentation/Components/PageBreadCrumb'
import useViewModel from './ViewModel'

const EXTERNAL_API_STATUS_BADGE: Record<string, string> = {
  not_sent: 'badge-light-secondary',
  success: 'badge-light-success',
  failed: 'badge-light-danger',
}

const EXTERNAL_API_STATUS_LABEL: Record<string, string> = {
  not_sent: 'Not Sent',
  success: 'Success',
  failed: 'Failed',
}

const ProjectPileScanTestView: React.FC = () => {
  const {
    scanCode,
    hasCamera,
    isUseCamera,
    handleScan,
    setScanCode,
    toggleUseCamera,
    scanResult,
    isSubmitting,
  } = useViewModel()

  return (
    <ViewPageTitle>
      <div className='mb-5'>
        <PageBreadCrumb />
      </div>

      <div className='card mb-5'>
        <div className='card-body'>
          <div className='row gy-5'>
            <div className='col-6'>
              <h3 className='fs-4'>Scan Receive Test</h3>
            </div>
            <div className='col-6'>
              <label>
                <span style={{ marginRight: 20 }}>ใช้งานกล้อง</span>
                <Switch onChange={toggleUseCamera} checked={isUseCamera} />
              </label>
            </div>
            {hasCamera && isUseCamera ? (
              <div className='col-12 d-flex justify-content-center'>
                <div style={{ maxWidth: 360, width: '100%' }}>
                  <Scanner onScan={handleScan} constraints={{ facingMode: 'environment' }} />
                </div>
              </div>
            ) : (
              <div className='col-12'>
                <p>No camera found</p>
                <form
                  onSubmit={(e: any) => {
                    e.preventDefault()
                    handleScan([{ rawValue: scanCode }])
                  }}>
                  <input
                    type='text'
                    onChange={(e: any) => setScanCode(e.target.value)}
                    value={scanCode}
                    className='form-control form-control-sm'
                    disabled={isSubmitting}
                  />
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {scanResult && (
        <div className='card shadow-sm'>
          <div className='card-header border-0 pt-6'>
            <div className='card-title'>
              <h3 className='fw-bold m-0'>Scan Result</h3>
            </div>
          </div>
          <div className='card-body pt-2'>
            <div
              className={`d-flex align-items-center rounded p-4 mb-6 ${
                scanResult.exists ? 'bg-light-success' : 'bg-light-danger'
              }`}>
              <span
                className={`bullet bullet-vertical h-40px me-4 ${
                  scanResult.exists ? 'bg-success' : 'bg-danger'
                }`}></span>
              <div className='d-flex flex-column'>
                <span
                  className={`fw-bold fs-5 ${
                    scanResult.exists ? 'text-success' : 'text-danger'
                  }`}>
                  {scanResult.exists ? 'Inventory Found' : 'Inventory Not Found'}
                </span>
                <span className='text-muted fs-7'>
                  {scanResult.exists
                    ? 'This serial exists in the system.'
                    : 'This serial does not exist in the system.'}
                </span>
              </div>
            </div>

            {scanResult.ambiguous && (
              <div className='d-flex align-items-center rounded p-4 mb-6 bg-light-danger'>
                <span className='bullet bullet-vertical h-40px me-4 bg-danger'></span>
                <div className='d-flex flex-column'>
                  <span className='fw-bold fs-5 text-danger'>Duplicate serial / ItemId</span>
                  <span className='text-muted fs-7'>
                    This serial/ItemId matches more than one pile - do not proceed with the real
                    scan.
                  </span>
                </div>
              </div>
            )}

            <div className='table-responsive'>
              <table className='table align-middle table-row-dashed gy-4 mb-0'>
                <tbody className='fs-6'>
                  <tr>
                    <td className='fw-semibold text-muted w-150px'>QR Code</td>
                    <td>
                      <code className='fs-6 text-gray-800'>{scanResult.qrcode}</code>
                    </td>
                  </tr>
                  <tr>
                    <td className='fw-semibold text-muted'>Serial</td>
                    <td>
                      <span className='fw-bold text-gray-800'>{scanResult.serial}</span>
                    </td>
                  </tr>
                  {scanResult.itemId && (
                    <tr>
                      <td className='fw-semibold text-muted'>ItemId</td>
                      <td>
                        <span className='fw-bold text-gray-800'>{scanResult.itemId}</span>
                      </td>
                    </tr>
                  )}
                  {scanResult.soNo && (
                    <tr>
                      <td className='fw-semibold text-muted'>SO No</td>
                      <td>
                        <span className='fw-bold text-gray-800'>{scanResult.soNo}</span>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className='fw-semibold text-muted'>Type</td>
                    <td>
                      <span
                        className={`badge fw-bold text-uppercase ${
                          scanResult.type === 'd365'
                            ? 'badge-light-primary'
                            : 'badge-light-info'
                        }`}>
                        {scanResult.type}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className='fw-semibold text-muted'>Inventory Exists</td>
                    <td>
                      <span
                        className={`badge fw-bold text-uppercase ${
                          scanResult.exists ? 'badge-light-success' : 'badge-light-danger'
                        }`}>
                        {scanResult.exists ? 'Yes' : 'No'}
                      </span>
                    </td>
                  </tr>
                  {scanResult.matchCount !== undefined && (
                    <tr>
                      <td className='fw-semibold text-muted'>Match Count</td>
                      <td>
                        <span
                          className={`badge fw-bold ${
                            scanResult.ambiguous ? 'badge-light-danger' : 'badge-light-success'
                          }`}>
                          {scanResult.matchCount}
                        </span>
                      </td>
                    </tr>
                  )}
                  {scanResult.exists && (
                    <tr>
                      <td className='fw-semibold text-muted'>Pile Code</td>
                      <td>
                        <span className='badge badge-light-primary fw-bold'>
                          {scanResult.inventory?.pileCode ?? '-'}
                        </span>
                      </td>
                    </tr>
                  )}
                  {scanResult.externalApiStatus && (
                    <tr>
                      <td className='fw-semibold text-muted'>External API Status</td>
                      <td>
                        <span
                          className={`badge fw-bold text-uppercase ${
                            EXTERNAL_API_STATUS_BADGE[scanResult.externalApiStatus] ??
                            'badge-light-secondary'
                          }`}>
                          {EXTERNAL_API_STATUS_LABEL[scanResult.externalApiStatus] ??
                            scanResult.externalApiStatus}
                        </span>
                      </td>
                    </tr>
                  )}
                  {scanResult.externalApiStatus === 'failed' &&
                    scanResult.externalApiStatusReason && (
                      <tr>
                        <td className='fw-semibold text-muted'>Reason</td>
                        <td>
                          <span className='fw-semibold text-danger'>
                            {scanResult.externalApiStatusReason}
                          </span>
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </ViewPageTitle>
  )
}

const ViewPageTitle: React.FC<WithChildren> = ({ children }) => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            isActive: false,
            isSeparator: false,
            path: '/project',
            title: 'Project',
            icon: '/media/icons/duotune/ecommerce/ecm006.svg',
          },
          {
            isActive: false,
            isSeparator: false,
            path: '#',
            title: 'Scan Test',
            icon: '/media/icons/duotune/ecommerce/ecm006.svg',
          },
        ]}>
        Project Pile Scan Receive Test
      </PageTitle>
      {children}
    </>
  )
}

export default ProjectPileScanTestView
